"use client";

import { type ReactNode, useState } from "react";
import type {
  AnsiSegment,
  BlockKind,
  InlineStyles,
} from "@/lib/tool-kit/random-fun/discord-markdown";

/*
 * A rough picture of how Discord draws each format, in its dark theme. The colours are
 * approximations used only here; Discord changes its look, and the preview says so.
 */

const SURFACE = "#313338";
const TEXT = "#dbdee1";
const MUTED = "#949ba4";
const CODE_BACKGROUND = "#2b2d31";
const SPOILER_COVER = "#1e1f22";
const LINK = "#00a8fc";

/** Approximate on-screen colours for ANSI text colours 30–37 and backgrounds 40–47. */
export const ANSI_FOREGROUND_HEX: Record<number, string> = {
  30: "#4f545c",
  31: "#dc322f",
  32: "#859900",
  33: "#b58900",
  34: "#268bd2",
  35: "#d33682",
  36: "#2aa198",
  37: "#ffffff",
};
export const ANSI_BACKGROUND_HEX: Record<number, string> = {
  40: "#002b36",
  41: "#cb4b16",
  42: "#586e75",
  43: "#657b83",
  44: "#839496",
  45: "#6c71c4",
  46: "#93a1a1",
  47: "#fdf6e3",
};

export function PreviewShell({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <section
      aria-label={label}
      className="space-y-1 rounded-lg p-4 text-sm leading-relaxed"
      style={{ backgroundColor: SURFACE, color: TEXT }}
    >
      {children}
    </section>
  );
}

function Spoiler({ children, label }: { children: ReactNode; label: string }) {
  const [shown, setShown] = useState(false);
  return (
    <button
      aria-label={label}
      aria-pressed={shown}
      className="rounded px-1 text-left"
      onClick={() => setShown(!shown)}
      style={{
        backgroundColor: shown ? "#3f4147" : SPOILER_COVER,
        color: shown ? TEXT : "transparent",
      }}
      type="button"
    >
      {children}
    </button>
  );
}

function inlineClass(styles: InlineStyles): string {
  return [
    styles.bold ? "font-bold" : "",
    styles.italic ? "italic" : "",
    styles.underline ? "underline" : "",
    styles.strike ? "line-through" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

const HEADING_CLASS: Partial<Record<BlockKind, string>> = {
  h1: "text-2xl font-bold",
  h2: "text-xl font-bold",
  h3: "text-lg font-bold",
};

function listMarker(block: BlockKind, number: number): string {
  if (block === "bullets") {
    return "\u2022";
  }
  return block === "numbers" ? `${number}.` : "";
}

export function StyledLine({
  text,
  styles,
  block,
  number,
  spoilerLabel,
}: {
  text: string;
  styles: InlineStyles;
  block: BlockKind;
  number: number;
  spoilerLabel: string;
}) {
  const trimmed = text.trim();
  if (trimmed === "") {
    return <div className="h-3" />;
  }
  let content: ReactNode = (
    <span className={inlineClass(styles)}>{trimmed}</span>
  );
  if (styles.spoiler) {
    content = <Spoiler label={spoilerLabel}>{content}</Spoiler>;
  }
  const marker = listMarker(block, number);
  const quoted = block === "quote" || block === "quoteBlock";
  return (
    <div
      className={`${HEADING_CLASS[block] ?? ""} ${block === "subtext" ? "text-xs" : ""} ${
        quoted ? "border-l-4 pl-3" : ""
      }`}
      style={{
        borderColor: quoted ? "#4e5058" : undefined,
        color: block === "subtext" ? MUTED : undefined,
      }}
    >
      {marker ? <span className="mr-2">{marker}</span> : null}
      {content}
    </div>
  );
}

export function PreviewLink({ text, url }: { text: string; url: string }) {
  return (
    <a
      className="underline-offset-2 hover:underline"
      href={url}
      onClick={(event) => event.preventDefault()}
      style={{ color: LINK }}
    >
      {text}
    </a>
  );
}

export function PreviewCode({ text, block }: { text: string; block: boolean }) {
  return block ? (
    <pre
      className="overflow-x-auto whitespace-pre-wrap rounded border p-3 font-mono text-xs"
      style={{ backgroundColor: CODE_BACKGROUND, borderColor: "#1e1f22" }}
    >
      {text}
    </pre>
  ) : (
    <code
      className="rounded px-1 font-mono text-xs"
      style={{ backgroundColor: CODE_BACKGROUND }}
    >
      {text}
    </code>
  );
}

export function PreviewAnsi({
  segments,
}: {
  segments: readonly AnsiSegment[];
}) {
  return (
    <pre
      className="overflow-x-auto whitespace-pre-wrap rounded border p-3 font-mono text-xs"
      style={{ backgroundColor: CODE_BACKGROUND, borderColor: "#1e1f22" }}
    >
      {segments.map((segment, index) => (
        <span
          className={`${segment.bold ? "font-bold" : ""} ${segment.underline ? "underline" : ""}`}
          // Segments are positional and their text can repeat.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional segments
          key={index}
          style={{
            color:
              segment.foreground === null
                ? undefined
                : ANSI_FOREGROUND_HEX[segment.foreground],
            backgroundColor:
              segment.background === null
                ? undefined
                : ANSI_BACKGROUND_HEX[segment.background],
          }}
        >
          {segment.text}
        </span>
      ))}
    </pre>
  );
}
